import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Flex, Loader, Modal, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';

import { InstagramPost } from '../../../../types/instagram-comments';

import { PostCard } from './PostCard';

import { axios } from '@/libraries/axios';

type Props = {
  pageId: string;
  pageName: string;
};

const fetchPosts = async (pageId: string): Promise<InstagramPost[]> => {
  const response: any = await axios.get('/meta/comments', {
    params: { pageId },
  });

  const comments = response.comments || [];
  const postsByMediaId = new Map<string, any>();

  comments.forEach((comment: any) => {
    const mediaId = comment.mediaId || 'unknown';

    if (!postsByMediaId.has(mediaId)) {
      postsByMediaId.set(mediaId, {
        _id: mediaId,
        mediaId: mediaId,
        mediaProductType: comment.mediaProductType || 'FEED',
        page: comment.page,
        commentsCount: 0,
        createdAt: comment.createdAt,
        lastCommentDate: comment.createdAt,
      });
    }

    const post = postsByMediaId.get(mediaId);
    post.commentsCount++;

    if (comment.createdAt > post.lastCommentDate) {
      post.lastCommentDate = comment.createdAt;
    }

    if (comment.createdAt < post.createdAt) {
      post.createdAt = comment.createdAt;
    }
  });

  return Array.from(postsByMediaId.values()).sort((a, b) => b.lastCommentDate - a.lastCommentDate);
};

export const PostsModal = NiceModal.create(({ pageId, pageName }: Props) => {
  const modal = useModal();

  const { data: posts, isLoading } = useQuery({
    queryKey: ['instagram-comments', pageId],
    queryFn: () => fetchPosts(pageId),
    enabled: modal.visible,
  });

  return (
    <Modal
      size="lg"
      centered
      overlayProps={{ opacity: 0.4, blur: 1.5 }}
      opened={modal.visible}
      onClose={() => modal.remove()}
      title={<Title order={3}>Posts with comments: {pageName}</Title>}
    >
      <ScrollArea h={600}>
        {isLoading ? (
          <Flex justify="center" align="center" h={200}>
            <Loader size="lg" />
          </Flex>
        ) : posts && posts.length > 0 ? (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Click on the post to see the comments.
            </Text>
            {posts.map((post) => (
              <PostCard key={post._id} post={post} pageName={pageName} />
            ))}
          </Stack>
        ) : (
          <Flex justify="center" align="center" h={200}>
            <Text c="dimmed" size="lg">
              Comments are absent.
            </Text>
          </Flex>
        )}
      </ScrollArea>
    </Modal>
  );
});
